import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/contexts/ProfileContext";

interface TrackEventParams {
  campaignId: string;
  placement: string;
  searchQuery?: string;
}

export function useAdTracking() {
  const { profile } = useProfile();

  const recordClick = useMutation({
    mutationFn: async ({ campaignId, placement, searchQuery }: TrackEventParams) => {
      const { data, error } = await supabase.rpc("record_ad_click", {
        p_campaign_id: campaignId,
        p_viewer_profile_id: profile?.id as string,
        p_placement: placement,
        p_search_query: searchQuery || undefined,
      });

      if (error) {
        console.error("Failed to record ad click:", error);
        return false;
      }

      return data;
    },
  });

  const recordImpression = useMutation({
    mutationFn: async ({ campaignId, placement }: Omit<TrackEventParams, "searchQuery">) => {
      const { data, error } = await supabase.rpc("record_ad_impression", {
        p_campaign_id: campaignId,
        p_viewer_profile_id: profile?.id as string,
        p_placement: placement,
      });

      if (error) {
        console.error("Failed to record ad impression:", error);
        return false;
      }

      return data;
    },
  });

  const recordImpressions = (campaigns: { campaignId: string; placement: string }[]) => {
    campaigns.forEach((c) => recordImpression.mutate(c));
  };

  return {
    recordClick: recordClick.mutate,
    recordImpression: recordImpression.mutate,
    recordImpressions,
    isRecordingClick: recordClick.isPending,
  };
}
